import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { shapeReport, categoryGrade, fixFor, whyFor } from './report/shapeReport';
import './NpdfGenerator.css'

// ===== Load PDF libraries (same as pdfGenerator.js) =====
const loadPDFLibraries = async () => {
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  window.jspdf = window.jspdf || window.jsPDF;
};

const THEMES = {
  light: {
    text: '#0f172a', textLight: '#64748b',
    bg: '#f8fafc', bgSecondary: 'rgba(0,0,0,0.03)',
    surface: '#ffffff', border: 'rgba(0,0,0,0.08)',
    primary: '#8b5cf6', primaryDark: '#7c3aed', primaryRgb: '139, 92, 246',
    danger: '#ef4444', warning: '#f59e0b', success: '#10b981'
  },
  dark: {
    text: '#f1f5f9', textLight: '#94a3b8',
    bg: '#0f172a', bgSecondary: 'rgba(255,255,255,0.04)',
    surface: '#1e293b', border: 'rgba(255,255,255,0.1)',
    primary: '#a78bfa', primaryDark: '#8b5cf6', primaryRgb: '167, 139, 250',
    danger: '#f87171', warning: '#fbbf24', success: '#34d399'
  }
};

const cssVarString = (t) => (
  `--text:${t.text};--textLight:${t.textLight};--bg:${t.bg};--bgSecondary:${t.bgSecondary};` +
  `--surface:${t.surface};--border:${t.border};--primary:${t.primary};--primaryDark:${t.primaryDark};` +
  `--primary-rgb:${t.primaryRgb};--danger:${t.danger};--warning:${t.warning};--success:${t.success};`
);

const PAGE_W = 794; // ~A4 @ 96dpi, matches pdfGenerator.js
const escapeHtml = (s = '') => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ===== Small shared renderers (mirror the deck's own JSX) =====
const sevPill = (severity) =>
  `<span class="deck-pill sev-${severity || 'info'}">${(severity || 'info').toUpperCase()}</span>`;

const logoHtml = (provider, size = '') => {
  const src = provider?.logo;
  const alt = provider?.name ? `${escapeHtml(provider.name)} logo` : 'Company logo';
  if (!src) return `<div class="deck-logo">🛡️</div>`;
  return `
    <div class="deck-logo report-logo">
      <img src="${src}" alt="${alt}" class="report-logo-img ${size === 'sm' ? 'report-logo-img-sm' : ''}"
           onerror="this.parentElement.outerHTML='<div class=&quot;deck-logo&quot;>🛡️</div>'" />
    </div>`;
};

const gaugeSvg = (score) => {
  const angle = (Math.max(0, Math.min(100, score)) / 100) * 180 - 90;
  return `
    <svg viewBox="0 0 200 118" class="deck-gauge" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="pdfGaugeGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#ef4444"/>
          <stop offset="55%" stop-color="#f59e0b"/>
          <stop offset="100%" stop-color="#10b981"/>
        </linearGradient>
      </defs>
      <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#pdfGaugeGrad)" stroke-width="14" stroke-linecap="round"/>
      <g transform="rotate(${angle} 100 100)">
        <line x1="100" y1="100" x2="100" y2="36" stroke="var(--text)" stroke-width="3" stroke-linecap="round"/>
        <circle cx="100" cy="100" r="6" fill="var(--text)"/>
      </g>
      <text x="20" y="115" class="deck-gauge-tick">0</text>
      <text x="180" y="115" text-anchor="end" class="deck-gauge-tick">100</text>
    </svg>`;
};

// A page wrapper: keeps the .deck-slide look (background gradients, spacing) but overrides
// the viewport-height/flex-centering rules that only make sense in a scrolling browser.
const page = (innerHtml, extraClass = '') => `
  <div class="deck-slide ${extraClass}" style="min-height:auto;height:auto;display:block;
       width:${PAGE_W}px;box-sizing:border-box;padding:44px 40px;">
    <div class="deck-slide-inner">${innerHtml}</div>
  </div>`;

// ===== Page builders =====

function buildTitleSlide(shaped, provider, providerName, prospectName, prospectCompany) {
  // "Prepared for" line — parity with the old cover page's prospect line for callers
  // (e.g. the internal prospecting tool) that pass prospectName/prospectCompany instead
  // of a tenant `provider` object.
  const preparedFor = prospectName
    ? `Prepared for: ${escapeHtml(prospectName)}${prospectCompany ? ` (${escapeHtml(prospectCompany)})` : ''}`
    : '';

  return page(`
    <div class="deck-center">
      ${logoHtml(provider)}
      ${providerName ? `<div class="deck-presenter-name">${escapeHtml(providerName)}</div>` : ''}
      <div class="deck-title-divider"></div>
      <h1 class="deck-main-title">${escapeHtml(shaped.domain)}</h1>
      <p class="deck-subtitle">${escapeHtml(shaped.reportTitle)}</p>
      <p class="deck-presented-by">${providerName ? `Presented by ${escapeHtml(providerName)}` : 'Powered by Pipsqueak NetScan'}</p>
      ${preparedFor ? `<p class="deck-presented-by" style="margin-top:6px;">${preparedFor}</p>` : ''}
      <p class="deck-meta" style="margin-top:40px;">
        🌐 ${escapeHtml(shaped.domain)} · Scanned ${new Date().toLocaleDateString()} · ${shaped.categoryCount} categories · ${shaped.totalFindings} findings
      </p>
    </div>
  `, 'deck-title-slide');
}

function buildSnapshotSlide(shaped) {
  const { data, riskLevel, grade, categories, criticalCount, highCount, mediumCount, lowCount, totalFindings } = shaped;
const exposureRows = categories.map(cat => {
  const g = categoryGrade(cat);
  return `
    <div class="deck-exposure-row">
      <span class="deck-grade grade-${g}">${g}</span>
      <span class="deck-exposure-name">${escapeHtml(cat.name)}</span>
      <span class="deck-exposure-count">${cat.findings?.length || 0} findings</span>
    </div>`;
}).join('');

  const calloutBody = (criticalCount + highCount) > 0
    ? `A <strong>${grade}</strong> grade with <strong>${criticalCount + highCount} critical/high finding${criticalCount + highCount === 1 ? '' : 's'}</strong> means attackers have a working path in — these gaps are visible to anyone who looks.`
    : `A <strong>${grade}</strong> grade with no critical or high findings — the remaining ${totalFindings} findings below are your hardening opportunities.`;

  return page(`
    <div class="deck-eyebrow">📊 EXTERNAL SCAN RESULTS</div>
    <h2 class="deck-heading">Executive Risk Snapshot</h2>
    <div class="deck-snapshot-grid">
  <div class="deck-score-severity-layout">

    <!-- GAUGE -->
    <div class="deck-panel deck-gauge-card">
      <div class="deck-panel-label">SECURITY SCORE</div>
      <div class="deck-score-gauge-wrap">
  ${gaugeSvg(data.riskScore)}
</div>
      <div class="deck-gauge-value ${riskLevel}">
        ${data.riskScore}<span>/100</span>
      </div>
      <div class="risk-badge ${riskLevel}">
        ${riskLevel.toUpperCase()} RISK
      </div>
    </div>

    <!-- 2 x 2 SEVERITY GRID -->
    <div class="deck-score-severity-grid">

      <div class="deck-panel deck-score-severity-tile">
        <div class="deck-sev-num sev-critical-text">${criticalCount}</div>
        <div class="deck-sev-name">CRITICAL</div>
      </div>

      <div class="deck-panel deck-score-severity-tile">
        <div class="deck-sev-num sev-high-text">${highCount}</div>
        <div class="deck-sev-name">HIGH</div>
      </div>

      <div class="deck-panel deck-score-severity-tile">
        <div class="deck-sev-num sev-medium-text">${mediumCount}</div>
        <div class="deck-sev-name">MEDIUM</div>
      </div>

      <div class="deck-panel deck-score-severity-tile">
        <div class="deck-sev-num sev-low-text">${lowCount}</div>
        <div class="deck-sev-name">LOW</div>
      </div>

    </div>
  </div>

  <!-- EXPOSURE -->
  <div class="deck-panel deck-exposure-card">
    <div class="deck-panel-label">EXTERNAL SURFACE EXPOSURE</div>
    <div class="deck-exposure-list">${exposureRows}</div>
  </div>
</div>
    <div class="deck-callout ${riskLevel}">${calloutBody}</div>
  `);
}

function buildFindingsTableSlide(shaped) {
  const { issueRows, allFindings, failRows, passRows } = shaped;
  const rows = (issueRows.length ? issueRows : allFindings.slice(0, 8));
  const tableRows = rows.map(f => `
    <tr>
      <td>${sevPill(f.severity)}</td>
      <td>
        <div class="deck-table-title">${escapeHtml(f.title)}</div>
        ${f.description ? `<div class="deck-table-desc">${escapeHtml(f.description)}</div>` : ''}
      </td>
      <td class="deck-table-cat">${escapeHtml(f.categoryName || '')}</td>
    </tr>`).join('');

  const failHtml = failRows.length ? `
    <div class="deck-panel deck-side-panel deck-side-fail">
      <div class="deck-panel-label">NEEDS ATTENTION</div>
      ${failRows.map(f => `<div class="deck-side-line">✗ <span>${escapeHtml(f.title)}${f.value ? ` <em>— ${escapeHtml(f.value)}</em>` : ''}</span></div>`).join('')}
    </div>` : '';
  const passHtml = passRows.length ? `
    <div class="deck-panel deck-side-panel deck-side-pass">
      <div class="deck-panel-label">WHAT'S WORKING</div>
      ${passRows.map(f => `<div class="deck-side-line">✓ <span>${escapeHtml(f.title)}</span></div>`).join('')}
    </div>` : '';

  return page(`
    <div class="deck-eyebrow">🚩 FINDINGS DETAIL</div>
    <h2 class="deck-heading">High-Risk Areas <span class="deck-heading-accent">Identified</span></h2>
    <div class="deck-findings-grid">
      <div class="deck-panel deck-table-panel" style="max-height:none;overflow:visible;">
        <table class="deck-table">
          <thead><tr><th>Severity</th><th>Finding</th><th>Category</th></tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
      <div class="deck-findings-side">${failHtml}${passHtml}</div>
    </div>
  `);
}

function buildImpactSlide(shaped) {
  if (!shaped.impactCards.length) return null;
  const cards = shaped.impactCards.map(c => `
    <div class="deck-panel deck-impact-card">
      <div class="deck-impact-icon">${c.icon}</div>
      <h4>${escapeHtml(c.head)}</h4>
      <p>${escapeHtml(c.body)}</p>
    </div>`).join('');
  return page(`
    <div class="deck-eyebrow">📉 BUSINESS RISK TRANSLATION</div>
    <h2 class="deck-heading">Operational Impact for <span class="deck-heading-accent">${escapeHtml(shaped.domain)}</span></h2>
    <div class="deck-impact-grid">${cards}</div>
  `);
}

function buildFixesSlide(shaped) {
  if (!shaped.challengeRows.length) return null;
  const rows = shaped.challengeRows.map(f => `
    <tr>
      <td><div class="deck-table-title">${escapeHtml(f.title)}</div><div class="deck-table-desc">${sevPill(f.severity)}</div></td>
      <td class="deck-fix-cell">${escapeHtml(fixFor(f))}</td>
      <td class="deck-fix-cell">${escapeHtml(whyFor(f))}</td>
    </tr>`).join('');
  return page(`
    <div class="deck-eyebrow">🧭 AT A GLANCE</div>
    <h2 class="deck-heading">Findings <span class="deck-heading-accent">→ Recommended Fixes</span></h2>
    <div class="deck-panel deck-table-panel" style="max-height:none;overflow:visible;">
      <table class="deck-table deck-fix-table">
        <thead><tr><th>Challenge</th><th>Recommended Fix</th><th>Why It Matters</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `);
}

function buildOutcomeSlide(shaped, providerName) {
  const { criticalCount, highCount, totalFindings, domain } = shaped;
  const card1 = (criticalCount + highCount) > 0
    ? `${criticalCount + highCount} critical/high finding${criticalCount + highCount === 1 ? '' : 's'} in this report ${criticalCount + highCount === 1 ? 'is' : 'are'} visible to anyone who scans ${escapeHtml(domain)}. Fixing them removes the easiest attack paths first.`
    : `No critical gaps today — hardening the ${totalFindings} findings in this report keeps it that way.`;
  const card4 = providerName
    ? `With ${escapeHtml(providerName)} monitoring your external surface, new exposures get caught and remediated before they're weaponized — while you focus on running your business.`
    : `Continuous monitoring of your external surface catches new exposures before they're weaponized — prevention costs far less than recovery.`;

  return page(`
    <div class="deck-eyebrow">🏆 THE OUTCOME</div>
    <h2 class="deck-heading">Why ${escapeHtml(domain)} Would Benefit <span class="deck-heading-accent">From Fixing This Now</span></h2>
    <div class="deck-outcome-grid">
      <div class="deck-outcome-card"><div class="deck-outcome-num">01</div><h4>Close the Gaps Before Attackers Find Them</h4><p>${card1}</p></div>
      <div class="deck-outcome-card"><div class="deck-outcome-num">02</div><h4>Protect Customers &amp; Reputation</h4><p>Email impersonation and lookalike domains directly target the people who trust you. Closing those vectors protects your brand and every message you send.</p></div>
      <div class="deck-outcome-card"><div class="deck-outcome-num">03</div><h4>Meet Compliance Expectations</h4><p>The findings here map to frameworks like SOC 2, ISO 27001, and OWASP. Remediating them strengthens your posture ahead of audits and customer security reviews.</p></div>
      <div class="deck-outcome-card"><div class="deck-outcome-num">04</div><h4>Sleep Better at Night</h4><p>${card4}</p></div>
    </div>
  `);
}

function buildFinalSlide(shaped, provider, providerName) {
  const contactLines = [
    provider?.website ? `<div class="deck-contact-line">🌍 ${escapeHtml(provider.website)}</div>` : '',
    provider?.phone ? `<div class="deck-contact-line">📞 ${escapeHtml(provider.phone)}</div>` : '',
    provider?.email ? `<div class="deck-contact-line">✉️ ${escapeHtml(provider.email)}</div>` : '',
    provider?.address ? `<div class="deck-contact-line">📍 ${escapeHtml(provider.address)}</div>` : ''
  ].join('');

  return page(`
    <div class="deck-center">
      ${logoHtml(provider)}
      <h2 class="deck-main-title deck-final-title">Thank You</h2>
      <p class="deck-lead deck-center-lead">
        These findings show ${escapeHtml(shaped.domain)} from the outside-in.
        ${providerName ? `${escapeHtml(providerName)} can help you close these gaps before an attacker finds them.` : 'Closing these gaps now costs far less than recovering from a breach later.'}
      </p>
      ${provider?.tagline ? `<p class="deck-tagline">${escapeHtml(provider.tagline)}</p>` : ''}
      <div class="deck-contact">${contactLines}</div>
    </div>
  `, 'deck-final-slide');
}

// ===== Category pages (paginated) =====
// Each finding becomes a compact card — title, severity, description, and a short
// why-it-matters / recommended-fix pair — never the raw unbounded `detail` text.
function findingCardHtml(f) {
  return `
    <div class="deck-panel report-finding" style="margin-bottom:14px;">
      <div class="deck-detail-header" style="margin-bottom:8px;">
        ${sevPill(f.severity)}
        <h3 class="deck-detail-title" style="font-size:16px;">${escapeHtml(f.title)}</h3>
      </div>
      ${f.description ? `<p class="deck-detail-desc" style="margin-bottom:10px;">${escapeHtml(f.description)}</p>` : ''}
      <div class="report-detail" style="margin-top:0;">
        <div class="detail-label-line"><span class="detail-label">Why it matters:</span> <span class="detail-label-value">${escapeHtml(whyFor(f))}</span></div>
        <div class="detail-label-line" style="margin-top:6px;"><span class="detail-label">Recommended fix:</span> <span class="detail-label-value">${escapeHtml(fixFor(f))}</span></div>
      </div>
    </div>`;
}

function categoryHeaderHtml(category, index, categoryCount, continued = false) {
  const catFindings = category.findings || [];
  const catPass = catFindings.filter(f => f.status === 'pass').length;
  const catWarn = catFindings.filter(f => f.status === 'warning').length;
  const catFail = catFindings.filter(f => f.status === 'fail').length;
  const g = categoryGrade(category);
  return `
    <div class="deck-eyebrow">🚩 CATEGORY ${index + 1} / ${categoryCount}</div>
    <h2 class="deck-heading">${escapeHtml(category.name)}${continued ? ' <span class="deck-heading-accent">(Continued)</span>' : ''}</h2>
    <div class="deck-cat-stats">
      <span class="deck-grade grade-${g}">${g}</span>
      <span class="deck-cat-chip">${catFindings.length} finding${catFindings.length === 1 ? '' : 's'}</span>
      ${catPass > 0 ? `<span class="deck-cat-chip chip-pass">✓ ${catPass} passed</span>` : ''}
      ${catWarn > 0 ? `<span class="deck-cat-chip chip-warn">⚠ ${catWarn} warning${catWarn === 1 ? '' : 's'}</span>` : ''}
      ${catFail > 0 ? `<span class="deck-cat-chip chip-fail">✗ ${catFail} failed</span>` : ''}
    </div>
  `;
}

// Measures rendered height of an HTML string at PAGE_W to decide when a category needs
// to spill onto a continuation page — same technique pdfGenerator.js already uses.
function measureHeight(html) {
  const probe = document.createElement('div');
  probe.style.cssText = `width:${PAGE_W}px;padding:44px 40px;box-sizing:border-box;visibility:hidden;position:absolute;left:-99999px;`;
  probe.className = 'deck-slide';
  probe.innerHTML = `<div class="deck-slide-inner">${html}</div>`;
  document.body.appendChild(probe);
  const h = probe.offsetHeight;
  document.body.removeChild(probe);
  return h;
}

const MAX_PAGE_HEIGHT = 1120; // leaves room for the footer band added at render time

function buildCategoryPages(shaped) {
  const pages = [];
  shaped.categories.forEach((category, index) => {
    const findings = category.findings || [];
    const header = categoryHeaderHtml(category, index, shaped.categoryCount);
    let bodyHtml = '';
    let cursor = 0;
    let isFirstPageForCategory = true;

    while (cursor < findings.length) {
      const pageHeader = isFirstPageForCategory ? header : categoryHeaderHtml(category, index, shaped.categoryCount, true);
      let pageBody = '';
      let addedAny = false;

      while (cursor < findings.length) {
        const candidate = pageBody + findingCardHtml(findings[cursor]);
        if (measureHeight(pageHeader + `<div>${candidate}</div>`) > MAX_PAGE_HEIGHT && addedAny) break;
        pageBody = candidate;
        cursor++;
        addedAny = true;
      }

      pages.push(page(pageHeader + `<div>${pageBody}</div>`, 'deck-category-slide'));
      isFirstPageForCategory = false;
    }

    if (findings.length === 0) {
      pages.push(page(header + `<div class="deck-detail-empty">No findings in this category.</div>`, 'deck-category-slide'));
    }
    void bodyHtml; // (kept for readability of the loop above)
  });
  return pages;
}

// ===== Main export =====
export const downloadReport = async (results, domain, options = {}) => {
  const {
    theme = 'light',
    isCyberMode = false,      // accepted for drop-in parity with the old signature; the
                               // deck design doesn't have a distinct cyber page skin, so
                               // this currently just guarantees a valid theme (see below)
                               // instead of silently dropping the flag.
    provider = null,
    providerName = provider?.name || '',
    prospectName = '',
    prospectCompany = '',     // accepted for drop-in parity — shown on the cover slide
    saveFile = true,
    fileName = null,
    onProgress = null
  } = options;

  const report = (step, msg) => { if (typeof onProgress === 'function') onProgress(step, msg); };
  report(0, 'Preparing slide-style report...');

  const shaped = shapeReport(results, domain, providerName, options.reportType);
  const resolvedTheme = THEMES[theme] ? theme : (isCyberMode ? 'dark' : 'light');
  const t = THEMES[resolvedTheme] || THEMES.light;

  report(15, 'Building pages...');
  const pages = [
    buildTitleSlide(shaped, provider, providerName, prospectName, prospectCompany),
    buildSnapshotSlide(shaped),
    buildFindingsTableSlide(shaped),
    buildImpactSlide(shaped),
    buildFixesSlide(shaped),
    ...buildCategoryPages(shaped),
    buildOutcomeSlide(shaped, providerName),
    buildFinalSlide(shaped, provider, providerName)
  ].filter(Boolean);

  report(30, 'Loading PDF libraries...');
  await loadPDFLibraries();

  // Off-screen render root. CSS vars are set inline here so `.deck-*` / `.report-*` rules
  // (already loaded globally via PipsqueakWidget.css) resolve correctly for this theme.
  const root = document.createElement('div');
  root.style.cssText = `position:absolute;left:-9999px;top:0;width:${PAGE_W}px;${cssVarString(t)}background:${t.bg};`;
  document.body.appendChild(root);

  const canvases = [];
  for (let i = 0; i < pages.length; i++) {
    root.innerHTML = pages[i];
    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(root, {
      scale: 2.5,
      useCORS: true,
      allowTaint: false,
      backgroundColor: t.bg,
      logging: false,
      windowWidth: PAGE_W,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.textContent = `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
          * { font-family: 'Inter', sans-serif !important; }
        `;
        clonedDoc.head.appendChild(style);
      }
    });
    canvases.push(canvas);
    report(30 + Math.round((i / pages.length) * 55), `Rendering page ${i + 1} of ${pages.length}...`);
  }

  document.body.removeChild(root);

  report(90, 'Assembling PDF...');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', hotfixes: ['px_scaling'] });
  const margin = 15;
  const pageWidth = 210;
  const contentWidth = pageWidth - margin * 2;

  canvases.forEach((canvas, i) => {
    const scale = contentWidth / canvas.width;
    if (i > 0) pdf.addPage();
    pdf.setFillColor(t.bg);
    pdf.rect(0, 0, 210, 297, 'F');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, contentWidth, canvas.height * scale, undefined, 'FAST');

    pdf.setDrawColor(139, 92, 246);
    pdf.setLineWidth(0.5);
    pdf.line(margin, 297 - 22, pageWidth - margin, 297 - 22);
    pdf.setFontSize(8);
    pdf.setTextColor(t.textLight);
    pdf.setFont('helvetica', 'normal');
    const footerY = 297 - 13;
    pdf.text('Confidential Security Report', margin, footerY);
    pdf.text(`Page ${i + 1} of ${canvases.length} · ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY, { align: 'center' });
    pdf.text(prospectName ? `Prepared for: ${prospectName}` : 'Generated by Security Scanner', pageWidth - margin, footerY, { align: 'right' });
  });

  report(100, 'Done!');

  if (saveFile) {
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedDomain = domain.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    pdf.save(fileName || `security-report-${sanitizedDomain}-${timestamp}.pdf`);
    return null;
  }
  return pdf.output('blob');
};

export default downloadReport;